import { useMutation, useQuery } from "@apollo/client/react";
import { CameraOutlined, CheckOutlined } from "@ant-design/icons";
import { Button, Card, Empty, Flex, Image, Masonry, Modal, QRCode, Space, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DeleteStagingFileDocument,
  GenerateCameraUrlDocument,
  GetStagingFilesDocument,
  type StagingFileItemFragment,
} from "../gql/generated/graphqlSchema.tsx";
import { usePageVisible } from "../hooks/usePageVisible.ts";
import googleQrCodeImg from "../assets/upload/google-qr-code.jpg";
import { formatDateTime } from "../utils/datetime.ts";
import { DeletableImageFile } from "./DeletableImageFile.tsx";
import { dateOverlay } from "./DeletableImageFile.styles.ts";
import { css } from "@linaria/core";

const hint = css`
  text-align: center;
`;
const stagingBody = css`
  max-height: calc(100vh - 500px);
  overflow-y: auto;
`;

const selectablePhoto = css`
  border-radius: var(--ant-border-radius);
  background-color: var(--ant-color-border-secondary);
  padding-bottom: var(--ant-padding-xs);
`;

type PhoneUploadModalProps = {
  readonly onSelect: (stagingFileId: string) => void;
};

const QR_REFRESH_INTERVAL = 50_000; // 50 seconds
const STAGING_POLL_INTERVAL = 2_000; // 2 seconds

export const PhoneUploadModal = ({ onSelect }: PhoneUploadModalProps) => {
  const { t, i18n } = useTranslation();
  const visible = usePageVisible();
  const [open, setOpen] = useState(false);
  const polling = open && visible;

  const [generateUrl, { data: urlData }] = useMutation(GenerateCameraUrlDocument);
  const { data: stagingData, loading } = useQuery(GetStagingFilesDocument, {
    skip: !polling,
    pollInterval: polling ? STAGING_POLL_INTERVAL : 0,
  });
  const [deleteStagingFile] = useMutation(DeleteStagingFileDocument);

  const cameraUrl = urlData?.generateCameraUrl;
  const fullUrl = cameraUrl ? `${window.location.origin}${cameraUrl}` : "";
  const stagingFiles = stagingData?.stagingFiles ?? [];

  const refreshUrl = useCallback(() => {
    void generateUrl();
  }, [generateUrl]);

  useEffect(() => {
    if (!polling) return;
    refreshUrl();
    const interval = setInterval(refreshUrl, QR_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [polling, refreshUrl]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSelect = useCallback(
    (stagingFileId: string) => {
      onSelect(stagingFileId);
      handleClose();
    },
    [onSelect],
  );

  const renderStagingFile = useCallback(
    (itemInfo: { data: StagingFileItemFragment }) => {
      const stagingFile = itemInfo.data;
      return (
        <Flex align="center" className={selectablePhoto} gap="small" vertical>
          <DeletableImageFile
            file={stagingFile}
            onDelete={() =>
              deleteStagingFile({ variables: { id: stagingFile.id }, refetchQueries: [GetStagingFilesDocument] })
            }
          >
            <div className={dateOverlay}>{formatDateTime(stagingFile.createdAt, i18n.language)}</div>
          </DeletableImageFile>
          <Button icon={<CheckOutlined />} onClick={() => handleSelect(stagingFile.id)} size="small" type="primary">
            {t("phone.select")}
          </Button>
        </Flex>
      );
    },
    [deleteStagingFile, handleSelect, i18n.language, t],
  );

  const masonryItems = stagingFiles.map((file) => ({ key: file.id, data: file }));

  return (
    <>
      <Button color="primary" icon={<CameraOutlined />} onClick={handleOpen} variant="outlined">
        {t("phone.modalButton")}
      </Button>

      <Modal destroyOnHidden footer={null} onCancel={handleClose} open={open} title={t("phone.modalTitle")} width={640}>
        <Flex gap="large" vertical>
          <Flex align="center" gap="large">
            {fullUrl ? (
              <Space>
                <QRCode bordered size={180} value={fullUrl} />
              </Space>
            ) : null}
            <Flex align="center" gap="small" vertical>
              <Typography.Text className={hint} type="secondary">
                {t("phone.modalHint")}
              </Typography.Text>
              <Space>
                <Image height={100} src={googleQrCodeImg} />
              </Space>
            </Flex>
          </Flex>

          {stagingFiles.length > 0 ? (
            <Card classNames={{ body: stagingBody }} size="small" title={t("phone.recentPhotos")}>
              <Image.PreviewGroup>
                <Masonry columns={3} gutter={[8, 8]} itemRender={renderStagingFile} items={masonryItems} />
              </Image.PreviewGroup>
            </Card>
          ) : (
            <Card loading={loading}>
              <Empty description={t("phone.noPhotos")} />
            </Card>
          )}
        </Flex>
      </Modal>
    </>
  );
};
